 
import updateProductInfoProductCompare from "@bevco/Store/Handlers/ProductCompare/UpdateProductInfo";
import updateProductInfoProductDetails from "@bevco/Store/Handlers/ProductDetails/UpdateProductInfo";
import updateProductInfoProductList from "@bevco/Store/Handlers/ProductList/UpdateProductInfo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import debounce from "lodash/debounce";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    // vernale customization start
    extendedStyles?: ProductQuantityOrderedStylesProps;
    customUOM?: string;
    labelPosition?: "left" | "top";
    numberOfSpools?: number;
    // vernale customization end
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        pageType: getCurrentPage(state).type, // Verndale Customization: Putup price
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
    };
};

// Verndale Customization: Putup price
const mapDispatchToProps = {
    updateProductInfoProductCompare,
    updateProductInfoProductDetails,
    updateProductInfoProductList,
};

type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasProductContext;

// Verndale Customization: Add custom styles
export interface ProductQuantityOrderedStylesProps {
    wrapper?: InjectableCss;
    productQuantityOrderedStyles?: TextFieldPresentationProps;
}

export const productQuantityOrderedStyles: ProductQuantityOrderedStylesProps = {
    wrapper: {
        css: css`
            position: relative;
            flex: 1 1 auto;
            min-width: 90px;

            &.left-aligned {
                &&&& ${FormInputWrapper} {
                    flex: 1 1 50% !important;
                    margin: 0;
                }

                &&&& ${FormFieldStyle} {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-end;
                    gap: 10px;
                    label {
                        flex: 1 1 45%;
                        padding: 0;
                        text-align: right;
                    }
                }
            }

            &.top-aligned {
                label {
                    margin: 0;
                    font-size: 10px;
                    text-transform: uppercase;
                    font-weight: 600;
                    margin-bottom: -15px;
                    padding-top: 10px;
                }
            }
        `,
    },

    productQuantityOrderedStyles: {
        cssOverrides: {
            formInputWrapper: css`
                input[type="number"]::-webkit-outer-spin-button,
                input[type="number"]::-webkit-inner-spin-button {
                    /* ISC-11023 deal with this. */
                    /* stylelint-disable-next-line */
                    -webkit-appearance: none;
                    margin: 0;
                }
            `,

            inputSelect: css`
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -moz-appearance: textfield;
            `,
        },
    },
};

// END Verndale Customization: Add custom styles

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, properties },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory, unitOfMeasure, pricing },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    configurationSelection,
    variantSelectionCompleted,
    customUOM,
    labelPosition,
    updateProductInfoProductCompare, // Verndale Customization: Putup price
    updateProductInfoProductDetails, // Verndale Customization: Putup price
    updateProductInfoProductList, // Verndale Customization: Putup price
    pageType, // Verndale Customization: Putup price
    numberOfSpools, // Verndale Customization: Putup price
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered > 0 ? qtyOrdered.toString() : "1");
    const [hasBreakPrices, setHasBreakPrices] = useState(false);

    // VERNDALE CUSTOMIZATION START: Adding uom to qty field
    const [unitOfMeasurePosition, setUnitOfMeasurePosition] = useState(0);
    const [labelWidth, setLabelWidth] = useState(0);
    // VERNDALE CUSTOMIZATION END: Adding uom to qty field

    // Verndale Customization Start: Putup price
    const allowedPages = ["ProductDetailsPage", "ProductListPage", "CartPage", "MyListsDetailsPage", "ProductComparePage"];
    const isPutUpPrice = pricing && pricing?.additionalResults?.spools;
    const [typingTimeout, setTypingTimeout] = useState<any | null>(null);
    const hasValidatedRef = useRef(false);
    // Verndale Customization End: Putup price

    useEffect(() => {
        const effectiveQty = qtyOrdered > 0 ? qtyOrdered : 1;
        if (effectiveQty === Number(productQty)) {
            return;
        }
        setProductQty(effectiveQty.toString());
    }, [qtyOrdered]);

    useEffect(() => {
        if (pricing?.actualBreakPrices) {
            setHasBreakPrices(true);
        }
    }, [pricing?.actualBreakPrices]);

    // Verndale Customization Start: Putup price
    const updateProductInfoHandler = (value: string) => {
        if (pageType === "ProductDetailsPage") {
            updateProductInfoProductDetails({
                productId,
                qtyOrdered: parseInt(value, 10) ?? 1,
                unitOfMeasure,
                numberOfSpools: numberOfSpools ?? 1,
            });
        }
        if (pageType === "ProductListPage") {
            updateProductInfoProductList({
                productId,
                qtyOrdered: parseInt(value, 10) ?? 1,
                unitOfMeasure,
                numberOfSpools: numberOfSpools ?? 1,
            });
        }
        if (pageType === "ProductComparePage") {
            updateProductInfoProductCompare({
                productId,
                qtyOrdered: parseInt(value, 10) ?? 1,
                unitOfMeasure,
                numberOfSpools: numberOfSpools ?? 1,
            });
        }
    };
    // Verndale Customization End: Putup price

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber) && !isPutUpPrice) {
            onQtyOrderedChanged(possibleNumber);
        }

        // // Verndale Customization Start: Putup price
        if (typingTimeout) {
            clearTimeout(typingTimeout);
        }

        const timeoutId = setTimeout(() => {
            if (allowedPages.includes(pageType) && isPutUpPrice) {
                onQtyOrderedChanged(possibleNumber);
                updateProductInfoHandler(value);
            } else if (!hasBreakPrices) {
                if (!Number.isNaN(possibleNumber)) {
                    onQtyOrderedChanged(possibleNumber);
                }
                updateProductInfoHandler(value);
            }
        }, 500);

        setTypingTimeout(timeoutId);
        // // Verndale Customization End: Putup price
    };

    // Verndale Customization: Add data attributes for unit of measure
    const getTextWidth = (text: string, font: string): number => {
        // re-use canvas object for better performance
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d") as CanvasRenderingContext2D;
        context!.font = font;
        const metrics = context!.measureText(text);
        return metrics.width;
    };

    const handleResize = debounce(() => {
        const element = document.querySelector('[data-test-selector="product_qtyOrdered-label"]') as HTMLElement;
        if (element) {
            setLabelWidth(element.offsetWidth + 25);
        }

        getTextWidth(productQty.toString(), "16px myriad-pro, sans-serif");
    }, 300);

    useEffect(() => {
        if (productQty) {
            setUnitOfMeasurePosition(getTextWidth(productQty.toString(), "16px myriad-pro, sans-serif"));
        }
    }, [productQty]);

    useEffect(() => {
        handleResize(); // Call on initial load

        window.addEventListener("resize", handleResize);

        // Cleanup
        return () => {
            handleResize.cancel(); // Cancel any pending debounced function calls
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (
            !hasValidatedRef.current && 
            properties?.isCmiProduct &&
            properties.isCmiProduct.toLowerCase() === "true"
        ) {
            hasValidatedRef.current = true;
    
            const customerLength = properties.customerLength;
            const standardPutup = properties.standardPutup;
    
            if (customerLength) {
                const possibleNumber = Number(customerLength.replace(/,/g, ""));
                const effectiveQty = !Number.isNaN(possibleNumber) && possibleNumber > 0 ? possibleNumber : 1;
                setProductQty(effectiveQty.toString());
                if (onQtyOrderedChanged) {
                    onQtyOrderedChanged(effectiveQty);
                }
        
            } else if (standardPutup) {
                const possibleNumber = Number(standardPutup.replace(/,/g, ""));
                const effectiveQty = !Number.isNaN(possibleNumber) && possibleNumber > 0 ? possibleNumber : 1;
                setProductQty(effectiveQty.toString());
                if (onQtyOrderedChanged) {
                    onQtyOrderedChanged(effectiveQty);
                }
            }
        }
    }, [properties]);
    // END Verndale Customization

    return (
        <StyledWrapper {...styles.wrapper} className={labelPosition === "left" ? "left-aligned" : "top-aligned"}>
            <TextField
                label={labelOverride ?? translate("QTY")}
                value={productQty}
                type="number"
                min={0}
                max={999999}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles.productQuantityOrderedStyles}
                // Verndale Customization: Add data attributes for unit of measure
                data-unit-of-measure={customUOM}
                onKeyUp={event => {
                    if (event.currentTarget.value.length > 0) {
                        setUnitOfMeasurePosition(
                            getTextWidth(event.currentTarget.value, "16px myriad-pro, sans-serif"),
                        );
                    }
                }}
                // END Verndale Customization
            />
            <span
                data-test-selector="product_unitOfMeasure"
                style={{
                    position: "absolute",
                    top: labelPosition === "left" ? "31%" : "60%",
                    left:
                        labelPosition === "left"
                            ? `${unitOfMeasurePosition + labelWidth}px`
                            : `${unitOfMeasurePosition + 15}px`,
                    fontSize: "12px",
                }}
            >
                {unitOfMeasure}
            </span>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductQuantityOrdered));
